import { Spinner } from "@blueprintjs/core";
import * as React from "react";
import { useParams } from "react-router";

import {
  DrupalPedigreeDuplicataRequestDto,
  EPlatform,
  ERequestState,
  PedigreeDuplicataRequestApi,
  RequestDto,
} from "../../../../api";
import { PageContainer } from "../../../../components";
import { useApiService, useCrudApi, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DrupalRequestResume, DrupalStepper } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";
import { EDrupalRoutes } from "../../../EDrupalRoutes";
import { DrupalDuplicataForm } from "./DrupalDuplicataForm";

export interface IDrupalPedigreeDuplicataRequestPageProps {}

export const DrupalPedigreeDuplicataRequestPage: React.FunctionComponent<IDrupalPedigreeDuplicataRequestPageProps> = () => {
  const { id } = useParams<{ id: string }>();
  const pedigreeDuplicataRequestId = React.useMemo(() => +id, [id]);

  const { t } = useTl();
  const api = useApiService(PedigreeDuplicataRequestApi);

  const [currentStep, setCurrentStep] = React.useState(0);

  const { data, loading, saveItem, saving } = useCrudApi<DrupalPedigreeDuplicataRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (apiId = pedigreeDuplicataRequestId) =>
          apiId > 0
            ? api.getDrupalPedigreeDuplicataRequest(apiId)
            : new DrupalPedigreeDuplicataRequestDto({
                pedigreeDuplicataRequestId: pedigreeDuplicataRequestId,
                request: RequestDto.fromJS({
                  platform: EPlatform.PORTAL,
                  status: ERequestState.REQUESTED,
                }),
              }).toJSON(),
        saveApiFn: (d: DrupalPedigreeDuplicataRequestDto) => api.saveDrupalPedigreeDuplicataRequest(d),
        onSavedRoute: (d) =>
          d.request.status === ERequestState.BILLED
            ? `${EDrupalRoutes.pedigreeDuplicataRequest}/${d.pedigreeDuplicataRequestId}`
            : EDrupalRoutes.requestList,
        deleteApiFn: () => {},
      }),
      [api, pedigreeDuplicataRequestId]
    ),
    [pedigreeDuplicataRequestId]
  );

  return loading ? (
    <Spinner />
  ) : (
    <PageContainer>
      <DrupalFieldSet
        title={
          currentStep === 2
            ? `${t(ETLCodes.Payment)}: ${t(ETLCodes.PedigreeDuplicataRequest)}`
            : `${t(ETLCodes.NewRequest)}: ${t(ETLCodes.PedigreeDuplicataRequest)}`
        }
      >
        {data?.request?.status === ERequestState.REQUESTED ? <DrupalStepper stepNumber={currentStep} /> : null}

        <div>
          {pedigreeDuplicataRequestId <= 1 || data?.request?.status === ERequestState.REQUESTED ? (
            <DrupalDuplicataForm
              pedigreeDuplicataRequestId={pedigreeDuplicataRequestId}
              stepNumber={currentStep}
              initialdata={data}
              loading={saving}
              onStepChange={(newStep) => setCurrentStep(newStep)}
              save={(formikdata) => saveItem(formikdata)}
            />
          ) : saving ? (
            <Spinner />
          ) : (
            <DrupalRequestResume
              requestForm={
                <DrupalDuplicataForm
                  pedigreeDuplicataRequestId={pedigreeDuplicataRequestId}
                  stepNumber={currentStep}
                  initialdata={data}
                  loading={saving}
                  onStepChange={(newStep) => setCurrentStep(newStep)}
                  save={(formikdata) => saveItem(formikdata)}
                />
              }
              requestDto={data?.request}
              bill={null}
            />
          )}
        </div>
      </DrupalFieldSet>
    </PageContainer>
  );
};
